import testClient from "@/api/testClient";
import {useMonitorLogging} from '@/core/useMonitor/logging';
import constant from "../constant";

const {closeLostEventAfterHalting} = useMonitorLogging();

export const MonitoringModule = {
    namespaced: true,
    state: {
        isHalted: false, // flag that monitoring is stoped
        isUserStartBrowserMonitoring: false, // flag that is user start browser monitoring
        error: null,
        cameraId: null,
        complete: false,
        photoState: null,
        faceDetectionState: [],
        isUploadingImage: false,
        isCompleteImageUploaded: false,
        startedCompleteImageUpload: false,
        cameraConnected: false,
        cameraMonitoringStarted: false,
        introImageUploaded: false,
        cameraDevices: [],
        cameraPermissionStatus: null,
        monitorSize:null,
        isFullScreenEnabled: null,

    },
    mutations: {
        SET_ERROR(state, error) {
            state.error = error;
            state.isHalted = true
        },

        CLEAR_ERROR(state) {
            state.error = null;
            state.isHalted = false;
        },
        SET_COMPLETE(state) {
            state.complete = true;
        },
        SET_CAMERA_ID(state, cameraId) {
            state.cameraId = cameraId;
        },
        SET_CAMERA_CONNECTED(state) {
            state.cameraConnected = true;
        },
        SET_CAMERA_DISCONNECTED(state) {
            state.cameraConnected = false;
        },

        SET_FACE_DETECTION_STATE(state, detectedState) {
            state.faceDetectionState = detectedState;
        },
        SET_PHOTO_STATE(state, photo_state) {
            state.photoState = photo_state;
        },
        SET_IS_UPLOADING_IMAGE(state, value) {
            state.isUploadingImage = value;
        },
        START_COMPLETE_IMAGE_UPLOAD(state) {
            state.startedCompleteImageUpload = true;
        },
        SET_CAMERA_MONITORING_START(state, value) {
            state.cameraMonitoringStarted = value;
        },
        SET_USER_START_BROWSER_MONITORING(state, value) {
            state.isUserStartBrowserMonitoring = value;
        },
        SET_INTRO_IMAGE_UPLOADED(state) {
            state.introImageUploaded = true;
        },
        SET_CAMERA_DEVICES(state, cameraDeviceList) {
            state.cameraDevices = cameraDeviceList
        },
        SET_CAMERA_PERMISSION_STATUS(state, permissionStatus) {
            state.cameraPermissionStatus = permissionStatus;
        },
        SET_FULL_SCREEN_ENABLED(state, value){
            state.isFullScreenEnabled = value;
        },
        SET_COMPLETE_IMAGE_UPLOADED(state){
            state.isCompleteImageUploaded = true;
        }

    },
    actions: {
        haltMonitoring({commit, dispatch}, error) {
            // console.log('haltMonitoring')
            dispatch('Test/setBookmarkModalVisibility', false, {root: true});
            commit('SET_ERROR', error);
        },
        unHaltMonitoring({commit}) {
            closeLostEventAfterHalting().then(() => {
                commit('SET_USER_START_BROWSER_MONITORING', true)
                commit('CLEAR_ERROR');
            });
        },
        setFullScreenEnabled({commit}, value){
          commit('SET_FULL_SCREEN_ENABLED', value);
        },
        setComplete({commit}) {
            commit('SET_COMPLETE');
        },
        setCameraId({commit}, cameraId) {
            commit('SET_CAMERA_ID', cameraId);
        },
        setCameraConnected({commit}) {
            commit('SET_CAMERA_CONNECTED');
        },
        setCameraDisconnected({commit}) {
            commit('SET_CAMERA_DISCONNECTED');
        },

        setFaceDetectionState({commit}, state) {
            commit('SET_FACE_DETECTION_STATE', state);
        },
        setIsUploadingImage({commit}, value) {
            commit('SET_IS_UPLOADING_IMAGE', value);
        },
        startCompleteImageUpload({commit}) {
            commit('START_COMPLETE_IMAGE_UPLOAD');
        },
        setCameraMonitoringStart({commit}, value) {
            commit('SET_CAMERA_MONITORING_START', value);
        },
        setUserStartBrowserMonitoring({commit}, value) {
            commit('SET_USER_START_BROWSER_MONITORING', value);
        },
        setIntroImageUploaded({commit}) {
            commit('SET_INTRO_IMAGE_UPLOADED');
        },
        setCameraDevices({commit}, deviceList) {
            commit('SET_CAMERA_DEVICES', deviceList);
        },
        setCameraPermissionStatus({commit}, status) {
            commit('SET_CAMERA_PERMISSION_STATUS', status)
        },
        setCompleteImageUploaded({commit}){
            commit("SET_COMPLETE_IMAGE_UPLOADED" )
        },

        async submitEvent({commit, rootState, dispatch}, event) {
            const response = await testClient.submitMonitorEvent(event)
            if (response.data.data['result'] === 'ok') {
                return response.data.data.event_id;
            } else {
                return null;
            }
        },

        async submitImage({commit, rootState, dispatch}, {image_data, event_id}) {
            const response = await testClient.submitMonitorImage(image_data, event_id);
            if (response.data.data['photo_state']) {
                commit('SET_PHOTO_STATE', response.data.data['photo_state']);
            }
            return response.data
        },


    },
    getters: {
        isMonitoringHalted: state => state.isHalted,

        isMonitoringBrowserEnabled(state, getters, rootState) {
            let result = rootState.Test.settings.test?.proctor_user_monitor_mode ?? false;
            return result;
        },

        isMonitoringCameraEnabled(state, getters, rootState) {

            let result =  (rootState.Test.settings.test?.proctor_camera_person_verification || rootState.Test.settings.test?.proctor_camera_mode) ?? false;
            return result
        },

        isCompletePhotoUploaded(state) {
            return state.isCompleteImageUploaded || (state.photoState.completed_photo_allowed <= state.photoState.completed_photo_taken);
        },

        getFaceDetectionState: (state) => {
            return state.faceDetectionState;
        },

        isCameraConnected: (state) => {
            return state.cameraConnected;
        },

        didStartUploadCompleteImage(state) {
            return state.startedCompleteImageUpload;
        },

        isShowCameraSetupModal(state, getters, rootState) {
            /**
             * show camera setup modal when camera is not connected
             or camera monitoring not started during the test
             */

            if (rootState.Test.test_state === constant.STATUS_TEST_IN_PROGRESS &&
                (state.cameraConnected === false || state.cameraMonitoringStarted === false)) {
                return true;
            }
            return false;
        },
        numberOfFaceDetected(state) {
            let count = 0;

            if (state.faceDetectionState.hasOwnProperty('results')) {
                state.faceDetectionState.results.forEach((result) => {
                    if (result.confidence > 75) {
                        count++;
                    }
                })
            }
            return count;
        },
        cameraDeviceList(state) {
            return state.cameraDevices
        },
        cameraId(state) {
            if (state.cameraId === null) {
                return state.cameraDevices[0]?.deviceId;
            }
            return state.cameraId;
        }

    },
};

